import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { useEffectSkipFirstRender, useValueEqualMemo } from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { Guid } from "client_legacy/ndaw/guid";
import { InterestedParty } from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import { DimensionToRender } from "../../RenderState/common";
import { HubItemTileResourceId } from "client/Api/Workspace/workspace";
import { HubTileCategoryIds } from "./common";

export enum HubCardWidth {
	Regular = 0,
	Narrow = 1,
	UltraNarrow = 2,
	Wide = 3
}

export enum HubCardHeight {
	Regular = 0,
	Short = 1,
	UltraShort = 2,
	Long = 3
}

export type HubCardSizeInformation = {
	width: HubCardWidth;
	height: HubCardHeight;
	shouldHideSecondaryInfo: boolean;
};

export function useHubCardSizeInformation(dimension: DimensionToRender): HubCardSizeInformation {
	return useMemo(
		(): HubCardSizeInformation => {
			const { columnSpan, rowSpan } = dimension.dimension;
			const width: HubCardWidth = dimension.kind === "responsive" || columnSpan < 6
				? HubCardWidth.UltraNarrow
				: columnSpan < 8
					? HubCardWidth.Narrow
					: columnSpan < 17
						? HubCardWidth.Regular
						: HubCardWidth.Wide;

			const height: HubCardHeight = dimension.kind === "responsive" || rowSpan < 6
				? HubCardHeight.UltraShort
				: rowSpan < 8
					? HubCardHeight.Short
					: rowSpan < 10
						? HubCardHeight.Regular
						: HubCardHeight.Long;

			const shouldHideSecondaryInfo = width === HubCardWidth.UltraNarrow;

			return {
				width,
				height,
				shouldHideSecondaryInfo
			};
		},
		[dimension]
	);
};

type HubItemLiveInformationParty<TItem> = InterestedParty<Guid, HubItemTileResourceId> & {
	updateItems(page: LazyLoadPageResult<TItem>): void;
	updateLastPinnedItem(lastPinnedItem: TItem | null): void;
};

export type HubItemLiveInformationProvider<TItem, TInterests> = {
	itemKind: HubItemKind;

	register(
		party: HubItemLiveInformationParty<TItem>,
		interests: TInterests
	): void;

	unregister(party: HubItemLiveInformationParty<TItem>): void;

	loadMoreItems(
		categoryIds: HubTileCategoryIds,
		interests: TInterests,
		lastRetrievedItem: TItem,
		signal?: AbortSignal
	): Promise<LazyLoadPageResult<TItem>>;

	forceReload(): void;
};

export type LoadingState<TItem> = {
	isLoading: boolean;
	hasTooManyConsecutiveErrors: boolean;
	hasGlobalFailure: boolean;
	page: LazyLoadPageResult<TItem> | undefined;
	lastPinnedItem?: TItem | null;
};

export function useHubItemLiveInformation<TItem, TInterests>(
	provider: HubItemLiveInformationProvider<TItem, TInterests>,
	tabId: Guid,
	resourceId: HubItemTileResourceId | undefined,
	interests: TInterests | undefined
): [loadingState: LoadingState<TItem>, reload: () => void] {
	const [loadingState, setLoadingState] = useState<LoadingState<TItem>>({
		isLoading: true,
		hasGlobalFailure: false,
		hasTooManyConsecutiveErrors: false,
		page: undefined
	});

	const stableResourceId = useValueEqualMemo(
		(): HubItemTileResourceId | undefined => resourceId,
		[resourceId]
	);

	const stableInterests = useValueEqualMemo(
		(): TInterests | undefined => interests,
		[interests]
	);

	useEffectSkipFirstRender(
		(): void => {
			hasImplicitDependencyOn(stableResourceId);
			hasImplicitDependencyOn(provider);
			setLoadingState({
				isLoading: true,
				hasGlobalFailure: false,
				hasTooManyConsecutiveErrors: false,
				page: undefined
			});
		},
		[stableResourceId, provider]
	);

	useEffect(
		(): (() => void) | undefined => {
			if (isNil(stableInterests) || isNil(stableResourceId)) {
				return;
			}

			const party: HubItemLiveInformationParty<TItem> = {
				partyId: tabId,
				resourceId: stableResourceId,
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState((x: LoadingState<TItem>): LoadingState<TItem> => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: true,
						page: undefined
					}));
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState((x: LoadingState<TItem>): LoadingState<TItem> => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					}));
				},
				updateItems: (page: LazyLoadPageResult<TItem>): void => {
					setLoadingState((x: LoadingState<TItem>): LoadingState<TItem> => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: false,
						page
					}));
				},
				updateLastPinnedItem: (lastPinnedItem: TItem | null) => {
					setLoadingState((x: LoadingState<TItem>): LoadingState<TItem> => ({
						...x,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: false,
						lastPinnedItem
					}));
				}
			};

			setLoadingState((x: LoadingState<TItem>): LoadingState<TItem> => ({
				...x,
				isLoading: true
			}));

			provider.register(
				party,
				stableInterests
			);

			return () => {
				provider.unregister(party);
			};
		},
		[
			stableResourceId,
			provider,
			tabId,
			stableInterests
		]
	);

	const reload = useCallback(
		(): void => {
			setLoadingState((x) => ({
				...x,
				isLoading: true
			}));

			provider.forceReload();
		},
		[provider]
	);

	return [loadingState, reload];
}
